import {
    Box, capitalize,
    Stack,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {PersonalTyp, TaetigkeitsberichtInternInfo} from "../../api";
import DataTable from "../data/DataTable";
import StartEndFilter from "../StartEndFilter";
import {formatGermanNumber} from "../../utils/fomatters";
import useCustomFilters from "../data/useCustomFilters";

export type TaetigkeitsberichtFilterFreigabe = {
    search?: string;
    filtered?: string;
    startDate?: string;
    endDate?: string;
};

export type Taetigkeitsbericht = {
    id: string;
    baustelleId: string;
    datum: string;
    taetigkeitsberichtTyp: PersonalTyp;
    personaleintrag: Personaleintrag;
};

export type Personaleintrag = {
    id: string;
    mitarbeiterId?: string;
    baustelleId: string;
    taetigkeitsberichtId: string;
    datum: string;
    arbeitszeitNetto?: number;
    lohnkosten?: number;
    stundensatz?: number;
    bemerkung?: string;
    lieferscheinnummer?: string;
};

export type Props = Omit<
    DataRequest<TaetigkeitsberichtFilterFreigabe>,
    "filter"
> &
    Partial<Pick<DataRequest<TaetigkeitsberichtFilterFreigabe>, "filter">>;

const tableName: string = "taetigkeitsberichte-freigabe"

export default function TaetigkeitsberichtDataTableFreigabe({
                                                                ...input
                                                            }: Props) {
    const request = useDataRequest<TaetigkeitsberichtFilterFreigabe>({
        filter: {},
        ...input,
    });

    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <TaetigkeitsberichtResultsFreigabe
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<TaetigkeitsberichtFilterFreigabe>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<TaetigkeitsberichtFilterFreigabe>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            startDate: "",
            endDate: "",
        }
    });

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            startDate: currentCustomFilter.startDate,
            endDate: currentCustomFilter.endDate,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <>
                <SearchField
                    value={currentCustomFilter.search}
                    onChange={(val) => setCustomField("search", val)}
                />
                <StartEndFilter startDatum={currentCustomFilter.startDate}
                                endeDatum={currentCustomFilter.endDate}
                                setCustomField={setCustomField}
                />
            </>
        </Box>
    );
}

function TaetigkeitsberichtResultsFreigabe({
                                               request,
                                               setFilteredState,
                                           }: {
    request: DataRequestState<TaetigkeitsberichtFilterFreigabe>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<TaetigkeitsberichtFilterFreigabe>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();

    const ladeTaetigkeitsberichte: DataLoader<
        TaetigkeitsberichtFilterFreigabe,
        TaetigkeitsberichtInternInfo
    > = useCallback(
        async (params) =>
            (await getApi()).taetigkeitsberichte.sucheInterneTaetigkeitsberichte({
                freigegeben: false,
                ...params,
            }),
        [getApi]
    );

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName, "taetigkeitsberichte"]}
            loadData={ladeTaetigkeitsberichte}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open("/berichtfreigabe/" + row.id, "_blank");
                } else {
                    navigate("/berichtfreigabe/" + row.id);
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<TaetigkeitsberichtInternInfo>> {
    const {t: translation} = useT("taetigkeitsbericht");

    return useMemo(
        () => [
            {
                field: "mitarbeiter",
                headerName: capitalize(translation("activity-report-user")),
                flex: 1,
            },
            {
                field: "baustellenNummer",
                headerName: capitalize(translation("activity-report-construction-number")),
                flex: 1,
                align: "right",
                headerAlign: "right",
            },
            {
                field: "baustellenBezeichnung",
                headerName: capitalize(translation("activity-report-construction")),
                flex: 1,
            },
            {
                field: "datum",
                headerName: capitalize(translation("activity-report-date")),
                flex: 1,
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "arbeitszeitNetto",
                headerName: capitalize(translation("activity-report-time-net")),
                flex: 1,
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "bemerkung",
                headerName: capitalize(translation("activity-report-remark")),
                flex: 1,
            },
            {
                field: "bearbeitetUm",
                headerName: capitalize(translation("processing-time")),
                flex: 1.5,
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        const date = new Date(params.value);
                        const formattedDate = date.toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                        const formattedTime = date.toLocaleTimeString(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                        });
                        return `${formattedDate} ${formattedTime}`;
                    }
                    return "";
                },
            },
        ],
        [translation]
    );
}
